import React, { useState } from 'react';
import { 
  FileText, 
  Search, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft, 
  MoreHorizontal, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Receipt,
  FileCheck2,
  CalendarCheck,
  CalendarX,
  Eye,
  X,
  ImagePlus,
  Trash2,
  RefreshCw,
  RotateCcw,
  Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { format, isValid, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { ManagedDocument, DocumentType, PaymentCondition } from '../../types';

interface DocumentListProps {
  documents: ManagedDocument[];
  onAdd: (type: DocumentType) => void;
  onEdit: (doc: ManagedDocument) => void;
  onSettle: (doc: ManagedDocument) => void;
  onDeleteJournalEntry?: (doc: ManagedDocument) => void;
  onUpdateImage?: (docId: string, baseDocumentUrl: string | null) => Promise<void>;
  onRecalculateBalance?: (docId: string) => Promise<void>;
  onRecalculateAll?: () => Promise<void>;
}

export const DocumentList: React.FC<DocumentListProps> = ({ 
  documents, 
  onAdd, 
  onEdit, 
  onSettle, 
  onDeleteJournalEntry, 
  onUpdateImage, 
  onRecalculateBalance,
  onRecalculateAll
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'ALL' | 'PURCHASE' | 'SALE' | 'PENDING' | 'CANCELLED' | 'UNSETTLED'>('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);
  const [docToDeleteImage, setDocToDeleteImage] = useState<string | null>(null);
  const [docToDeleteJournalEntry, setDocToDeleteJournalEntry] = useState<ManagedDocument | null>(null);
  const [isRecalculating, setIsRecalculating] = useState<string | null>(null);

  const unsettledCount = documents.filter(d => !d.journalEntryId).length;

  const compressImage = async (file: File): Promise<Blob> => {
    if (file.type === 'application/pdf') return file;
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1200;
        let width = img.width;
        let height = img.height;

        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', 0.7);
      };
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingDocId || !onUpdateImage) return;

    try {
      const compressedBlob = await compressImage(file);
      const baseDocumentUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(compressedBlob);
      });
      await onUpdateImage(uploadingDocId, baseDocumentUrl);
    } catch (error) {
      console.error(error);
      alert("Error al procesar la imagen");
    } finally {
      setUploadingDocId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredDocs = documents.filter(doc => {
    const searchLower = search.toLowerCase();
    const matchesSearch = 
      doc.contactName.toLowerCase().includes(searchLower) ||
      (doc.locatorCode && doc.locatorCode.toLowerCase().includes(searchLower)) ||
      (doc.number && doc.number.toLowerCase().includes(searchLower));
    
    if (!matchesSearch) return false;

    if (dateFrom && doc.date < dateFrom) return false;
    if (dateTo && doc.date > dateTo) return false;

    if (filter === 'PURCHASE') return doc.type === 'PURCHASE_INVOICE' || doc.type === 'INTERNAL_EXPENSE' || doc.type === 'RECEIPT_EXPENSE';
    if (filter === 'SALE') return doc.type === 'SALES_INVOICE' || doc.type === 'INTERNAL_INCOME' || doc.type === 'RECEIPT_INCOME';
    if (filter === 'PENDING') return !doc.isCancelled && doc.condition === 'CREDITO';
    if (filter === 'CANCELLED') return doc.isCancelled;
    if (filter === 'UNSETTLED') return !doc.journalEntryId;
    
    return true;
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const getDocTypeInfo = (type: DocumentType) => {
    switch (type) {
      case 'SALES_INVOICE': return { label: 'Factura Venta', icon: ArrowUpRight, color: 'text-emerald-500', bg: 'bg-emerald-500/10' };
      case 'PURCHASE_INVOICE': return { label: 'Factura Compra', icon: ArrowDownLeft, color: 'text-red-500', bg: 'bg-red-500/10' };
      case 'INTERNAL_INCOME': return { label: 'Doc. Interno (I)', icon: ArrowUpRight, color: 'text-emerald-400', bg: 'bg-emerald-400/10' };
      case 'INTERNAL_EXPENSE': return { label: 'Doc. Interno (E)', icon: ArrowDownLeft, color: 'text-red-400', bg: 'bg-red-400/10' };
      case 'RECEIPT_INCOME': return { label: 'Recibo Ingreso', icon: Receipt, color: 'text-emerald-500', bg: 'bg-emerald-500/10' };
      case 'RECEIPT_EXPENSE': return { label: 'Recibo Egreso', icon: Receipt, color: 'text-red-500', bg: 'bg-red-500/10' };
      default: return { label: 'Documento', icon: FileText, color: 'text-zinc-400', bg: 'bg-zinc-400/10' };
    }
  };

  return (
    <div className="space-y-6">
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        accept="image/png, image/jpeg, image/jpg, application/pdf"
        onChange={handleFileChange} 
      />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input 
            type="text"
            placeholder="Buscar por contacto o número..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-800 rounded-xl pl-10 pr-4 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-zinc-300"
          />
          <span className="text-zinc-500">-</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all text-zinc-300"
          />
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {unsettledCount > 0 && (
            <button
              onClick={() => setFilter(filter === 'UNSETTLED' ? 'ALL' : 'UNSETTLED')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                filter === 'UNSETTLED' 
                  ? "bg-amber-500 text-black shadow-lg shadow-amber-500/20" 
                  : "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 border border-amber-500/20"
              }`}
            >
              <FileCheck2 size={16} />
              Pendientes de Asiento
              <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                filter === 'UNSETTLED' ? "bg-black/20" : "bg-amber-500/20"
              }`}>
                {unsettledCount}
              </span>
            </button>
          )}

          <select 
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2 text-sm text-zinc-400 focus:ring-2 focus:ring-emerald-500/50 outline-none transition-all"
          >
            <option value="ALL">Todos</option>
            <option value="UNSETTLED">Pendientes de Asiento</option>
            <option value="PURCHASE">Compras</option>
            <option value="SALE">Ventas</option>
            <option value="PENDING">Pendientes de Pago</option>
            <option value="CANCELLED">Cancelados</option>
          </select>

          {onRecalculateAll && (
            <button
              onClick={async () => {
                const confirmed = confirm("¿Estás seguro de que deseas recalcular los saldos de TODOS los documentos? Esto puede tomar un momento.");
                if (confirmed) {
                  setIsRecalculating('ALL');
                  await onRecalculateAll();
                  setIsRecalculating(null);
                }
              }}
              disabled={isRecalculating === 'ALL'}
              className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-400 hover:text-white px-4 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            >
              {isRecalculating === 'ALL' ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <RotateCcw size={16} />
              )}
              Recalcular Todo
            </button>
          )}

          <div className="relative">
            <button 
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-xl font-bold transition-all whitespace-nowrap shadow-lg shadow-emerald-600/20"
            >
              <Plus size={18} />
              Agregar
            </button>
            <AnimatePresence>
              {showAddMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowAddMenu(false)} />
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute right-0 mt-2 w-56 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl z-50 overflow-hidden"
                  >
                    <div className="p-2 space-y-1">
                      <button 
                        onClick={() => { onAdd('PURCHASE_INVOICE'); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 rounded-xl transition-all text-sm font-medium text-red-400"
                      >
                        <ArrowDownLeft size={16} /> Factura de Compra
                      </button>
                      <button 
                        onClick={() => { onAdd('SALES_INVOICE'); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 rounded-xl transition-all text-sm font-medium text-emerald-400"
                      >
                        <ArrowUpRight size={16} /> Factura de Venta
                      </button>
                      <div className="h-px bg-zinc-800 my-1 mx-2" />
                      <button 
                        onClick={() => { onAdd('INTERNAL_EXPENSE'); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 rounded-xl transition-all text-sm font-medium text-zinc-400"
                      >
                        <FileText size={16} /> Doc. Interno Egreso
                      </button>
                      <button 
                        onClick={() => { onAdd('INTERNAL_INCOME'); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 rounded-xl transition-all text-sm font-medium text-zinc-400"
                      >
                        <FileText size={16} /> Doc. Interno Ingreso
                      </button>
                      <div className="h-px bg-zinc-800 my-1 mx-2" />
                      <button 
                        onClick={() => { onAdd('RECEIPT_EXPENSE'); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 rounded-xl transition-all text-sm font-medium text-red-400"
                      >
                        <Receipt size={16} /> Recibo de Dinero (Egreso)
                      </button>
                      <button 
                        onClick={() => { onAdd('RECEIPT_INCOME'); setShowAddMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-800 rounded-xl transition-all text-sm font-medium text-emerald-400"
                      >
                        <Receipt size={16} /> Recibo de Dinero (Ingreso)
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden overflow-x-auto">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead>
            <tr className="bg-zinc-950 border-b border-zinc-800">
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Documento / Fiscal</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Fecha</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Contacto (Ext)</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Responsable (Int)</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Condición</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Estado</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-right">Total</th>
              <th className="px-6 py-4 text-[10px] font-bold text-zinc-500 uppercase tracking-widest text-center">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {filteredDocs.map(doc => {
              const { label, icon: Icon, color, bg } = getDocTypeInfo(doc.type);
              const isPending = doc.condition === 'CREDITO' && !doc.isCancelled;
              
              return (
                <tr key={doc.id} className="hover:bg-zinc-800/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${bg} border border-white/5 relative`}>
                        <Icon size={16} className={color} />
                        {doc.baseDocumentUrl && (
                          <button 
                            onClick={() => setPreviewImage(doc.baseDocumentUrl!)}
                            className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center text-white shadow-lg ring-2 ring-zinc-900 hover:scale-110 transition-transform cursor-pointer"
                            title="Ver Documento Original"
                          >
                            <Eye size={8} />
                          </button>
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-bold text-white">{label}</div>
                          {doc.isHistorical && (
                            <span className="text-[8px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-widest border border-indigo-500/30 flex items-center gap-1">
                              <Clock size={8} /> Histórico
                            </span>
                          )}
                          {doc.locatorCode && (
                            <span className="text-[9px] font-mono font-bold bg-white/10 text-white px-1.5 py-0.5 rounded tracking-widest border border-white/20">
                              Ref: {doc.locatorCode}
                            </span>
                          )}
                        </div>
                        <div className="flex flex-col gap-0.5 mt-0.5">
                          <div className="text-[10px] text-zinc-500 font-mono tracking-tight flex items-center gap-1">
                            <span>Nro: {doc.number || 'SN'}</span>
                            {doc.timbrado && <span className="opacity-50">• Timb: {doc.timbrado}</span>}
                          </div>
                          {doc.cdc && (
                             <div className="text-[9px] text-emerald-500/50 font-mono truncate max-w-[120px]" title={`CDC: ${doc.cdc}`}>
                               CDC: {doc.cdc.slice(0, 16)}...
                             </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-400">
                    {doc.date && isValid(new Date(doc.date + 'T12:00:00')) 
                      ? format(new Date(doc.date + 'T12:00:00'), "dd MMM, yyyy", { locale: es }) 
                      : doc.date || 'S/F'}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-zinc-300">
                    {doc.contactName}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-zinc-400 italic">
                    {doc.internalContactName}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider border ${
                      doc.condition === 'CONTADO' 
                        ? 'bg-zinc-800 text-zinc-400 border-zinc-700' 
                        : 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                    }`}>
                      {doc.condition}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      {doc.isCancelled ? (
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded-md border border-emerald-500/20 uppercase tracking-wider">
                            <CheckCircle2 size={12} /> Liquidado
                          </div>
                          {doc.condition === 'CONTADO' && (!doc.transactionIds || doc.transactionIds.length === 0) && (
                            <div className="group relative">
                              <AlertCircle size={14} className="text-amber-500 cursor-help" />
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-zinc-900 border border-zinc-800 rounded-lg text-[9px] text-zinc-400 hidden group-hover:block z-20 pointer-events-none">
                                Este documento está marcado como CONTADO pero no tiene movimientos financieros vinculados (posible desincronización).
                              </div>
                            </div>
                          )}
                        </div>
                      ) : doc.condition === 'CREDITO' ? (
                        <div className="flex items-center gap-1.5 text-[10px] font-bold text-amber-500 bg-amber-500/10 px-2 py-1 rounded-md border border-amber-500/20 uppercase tracking-wider">
                          <Clock size={12} /> Pendiente
                        </div>
                      ) : (
                        <div className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider">Registrado</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="text-sm font-bold text-white">{formatCurrency(doc.totalAmount)}</div>
                    {doc.currency && doc.currency !== 'PYG' && (
                      <div className="text-[10px] text-amber-500 font-bold">
                        {new Intl.NumberFormat('es-PY', { style: 'currency', currency: doc.currency }).format(doc.foreignTotalAmount || 0)}
                      </div>
                    )}
                    <div className="flex flex-col items-end gap-0.5 mt-0.5">
                      {isPending && doc.remainingAmount !== undefined && (
                        <div className="text-[10px] text-amber-500 font-medium">Faltan: {formatCurrency(doc.remainingAmount)}</div>
                      )}
                      {(doc.tax5Amount || doc.tax10Amount) && (
                        <div className="flex gap-2">
                          {doc.tax5Amount !== undefined && doc.tax5Amount > 0 && (
                            <span className="text-[9px] text-zinc-500 font-medium uppercase tracking-tighter">IVA 5%: {formatCurrency(doc.tax5Amount)}</span>
                          )}
                          {doc.tax10Amount !== undefined && doc.tax10Amount > 0 && (
                            <span className="text-[9px] text-zinc-500 font-medium uppercase tracking-tighter">IVA 10%: {formatCurrency(doc.tax10Amount)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                       {!doc.journalEntryId && (
                         <button 
                           onClick={() => onSettle(doc)}
                           title={doc.isHistorical ? "Generar Asiento de Apertura" : "Asentar Contablemente"}
                           className="p-1.5 text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-all"
                         >
                           <FileCheck2 size={16} />
                         </button>
                       )}
                       {doc.journalEntryId && (
                         <button 
                           onClick={() => setDocToDeleteJournalEntry(doc)}
                           title="Eliminar Asiento Contable vinculado"
                           className="p-1.5 text-emerald-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all group relative"
                         >
                           <CalendarCheck size={16} className="group-hover:opacity-0 transition-opacity" />
                           <CalendarX size={16} className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                         </button>
                       )}
                       {(doc.condition === 'CREDITO' || (doc.condition === 'CONTADO' && doc.isCancelled && (!doc.transactionIds || doc.transactionIds.length === 0))) && (
                          <button 
                            onClick={async () => {
                              if (onRecalculateBalance) {
                                setIsRecalculating(doc.id);
                                await onRecalculateBalance(doc.id);
                                setIsRecalculating(null);
                              }
                            }}
                            title="Recalcular Saldo Pendiente"
                            className="p-1.5 text-zinc-500 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-all"
                            disabled={isRecalculating === doc.id}
                          >
                            {isRecalculating === doc.id ? (
                               <Loader2 size={16} className="animate-spin" />
                            ) : (
                               <RefreshCw size={16} />
                            )}
                          </button>
                        )}
                        {doc.condition === 'CREDITO' && (
                          <button 
                            onClick={() => alert("Esta función ha sido deshabilitada según solicitud del usuario.")}
                           title="Ver/Cargar Pagos"
                           className="p-1.5 text-zinc-500 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-all"
                         >
                           <Receipt size={16} />
                         </button>
                       )}
                       
                       {onUpdateImage && !doc.baseDocumentUrl && (
                         <button 
                           onClick={() => {
                             setUploadingDocId(doc.id);
                             fileInputRef.current?.click();
                           }}
                           title="Subir Imagen de Respaldo"
                           className="p-1.5 text-zinc-500 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-lg transition-all"
                         >
                           <ImagePlus size={16} />
                         </button>
                       )}
                       {onUpdateImage && doc.baseDocumentUrl && (
                         <button 
                           onClick={() => {
                             if (true) {
                               setDocToDeleteImage(doc.id);
                             }
                           }}
                           title="Eliminar Imagen de Respaldo"
                           className="p-1.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                         >
                           <Trash2 size={16} />
                         </button>
                       )}

                       <button 
                         onClick={() => onEdit(doc)}
                         className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition-all"
                       >
                         <MoreHorizontal size={16} />
                       </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredDocs.length === 0 && (
          <div className="p-12 text-center text-zinc-500">
             No se encontraron documentos comerciales.
          </div>
        )}
      </div>

      <AnimatePresence>
        {previewImage && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }}
              onClick={() => setPreviewImage(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm" 
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-5xl w-full max-h-[95vh] flex flex-col bg-zinc-900 rounded-3xl overflow-hidden shadow-2xl border border-zinc-800"
            >
              <div className="flex justify-end p-4 absolute top-0 right-0 z-10 gap-2 pointer-events-none">
                <button 
                  onClick={() => setPreviewImage(null)}
                  className="pointer-events-auto p-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-xl shadow-lg transition-all"
                >
                  <X size={20} />
                </button>
              </div>
              <div className="p-4 overflow-auto flex-1 flex flex-col items-center bg-zinc-950 gap-8 pt-16 pb-12">
                {(() => {
                  let imgUrls: string[] = [];
                  if (previewImage.includes('|')) {
                    imgUrls = previewImage.split('|');
                  } else if (previewImage.includes('data:')) {
                    // Legacy comma-separated base64 urls
                    const dataUrls = previewImage.match(/data:[a-zA-Z0-9\/+-.]+;base64,[A-Za-z0-9+/=]+/g);
                    if (dataUrls && dataUrls.length > 0) {
                       imgUrls = dataUrls;
                    } else {
                       imgUrls = [previewImage];
                    }
                  } else if (previewImage.includes(',')) {
                    // Legacy comma-separated firebase storage URLs
                    imgUrls = previewImage.split(',');
                  } else {
                    imgUrls = [previewImage];
                  }

                  return imgUrls.map((imgUrl, idx) => {
                    const isDataUrl = imgUrl.startsWith('data:');
                    const isPdf = imgUrl.includes('application/pdf') || imgUrl.toLowerCase().endsWith('.pdf') || imgUrl.includes('.pdf?alt=media');
                    return (
                      <div key={idx} className="relative w-full flex flex-col items-center">
                        {!isPdf ? (
                          <img 
                            src={imgUrl.trim()} 
                            alt={`Documento Original ${idx + 1}`} 
                            className="max-w-full h-auto rounded-lg shadow-2xl object-contain bg-zinc-900"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="w-full max-w-md aspect-[3/4] bg-zinc-800 rounded-lg shadow-2xl flex flex-col items-center justify-center text-zinc-500 border border-zinc-700">
                            <FileText size={48} className="mb-4 opacity-50" />
                            <span className="font-medium text-lg">Documento PDF</span>
                          </div>
                        )}
                        <button
                          onClick={() => {
                            if (isDataUrl) {
                              const [header, base64] = imgUrl.split(',');
                              const mimeType = header.split(':')[1].split(';')[0];
                              const byteCharacters = atob(base64);
                              const byteNumbers = new Array(byteCharacters.length);
                              for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                              }
                              const byteArray = new Uint8Array(byteNumbers);
                              const blob = new Blob([byteArray], { type: mimeType });
                              const url = URL.createObjectURL(blob);
                              window.open(url, '_blank');
                              setTimeout(() => URL.revokeObjectURL(url), 1000);
                            } else {
                              window.open(imgUrl.trim(), '_blank');
                            }
                          }}
                          className="mt-4 flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg transition-all text-sm font-medium"
                          title={isPdf ? "Abrir PDF en pestaña nueva" : "Abrir imagen en pestaña nueva"}
                        >
                          <span>{isPdf ? `Abrir PDF ${idx + 1}` : `Abrir imagen ${idx + 1}`}</span>
                          <ArrowUpRight size={16} />
                        </button>
                      </div>
                    );
                  });
                })()}
              </div>
            </motion.div>
          </div>
        )}

        {docToDeleteImage && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-red-500/20 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl p-6 space-y-6"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center text-red-500">
                  <Trash2 size={32} />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">¿Eliminar imagen?</h3>
                  <p className="text-sm text-zinc-400 mt-2">
                    Estás a punto de eliminar la imagen de respaldo actual. Podrás subir una nueva después.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setDocToDeleteImage(null)}
                  className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => {
                    if (onUpdateImage) {
                      onUpdateImage(docToDeleteImage, null);
                    }
                    setDocToDeleteImage(null);
                  }}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  Sí, eliminar
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {docToDeleteJournalEntry && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-amber-500/20 rounded-3xl w-full max-w-sm overflow-hidden shadow-2xl p-6 space-y-6"
            >
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center text-amber-500">
                  <CalendarX size={32} />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">¿Eliminar asiento contable?</h3>
                  <p className="text-sm text-zinc-400 mt-2">
                    Estás a punto de desvincular y eliminar el asiento de este documento (<strong className="text-white">{docToDeleteJournalEntry.number || 'S/N'}</strong>). Deberás volver a generarlo luego.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={() => setDocToDeleteJournalEntry(null)}
                  className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-bold rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => {
                    if (onDeleteJournalEntry) {
                      onDeleteJournalEntry(docToDeleteJournalEntry);
                    }
                    setDocToDeleteJournalEntry(null);
                  }}
                  className="flex-1 py-3 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  Sí, eliminar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
